/* eslint-disable no-console */

export class DtlnNoiseSuppressionProcessor {
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private gainNode: GainNode | null = null;
  private processedTrack: MediaStreamTrack | null = null;

  async startProcessing(originalAudioTrack: MediaStreamTrack, gainValue: number = 1.8): Promise<MediaStreamTrack> {
    this.stopProcessing(); // Clean up any existing instances first

    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000,
      });

      // Load the audio-worklet file from the public directory
      await this.audioContext.audioWorklet.addModule("/audio-worklet.js");

      // Resume context if suspended
      await this.audioContext.resume();

      // Create stream and source node
      const mediaStream = new MediaStream([originalAudioTrack]);
      this.sourceNode = this.audioContext.createMediaStreamSource(mediaStream);

      // Create the AudioWorklet node
      this.workletNode = new AudioWorkletNode(this.audioContext, "NoiseSuppressionWorker", {
        processorOptions: { disableMetrics: true },
      });

      // Wait for the WASM module inside the worklet to signal it is ready
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("DTLN NoiseSuppressionWorker initialization timed out (5s)"));
        }, 5000);

        if (this.workletNode) {
          this.workletNode.port.onmessage = (event) => {
            if (event.data === "ready") {
              clearTimeout(timeout);
              resolve();
            }
          };
        }
      });

      // Create destination node
      this.destinationNode = this.audioContext.createMediaStreamDestination();

      // Create gain node to boost volume
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = gainValue;

      // Connect: Source -> Worklet -> Gain -> Destination
      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.gainNode);
      this.gainNode.connect(this.destinationNode);

      // Extract the processed track
      this.processedTrack = this.destinationNode.stream.getAudioTracks()[0];

      return this.processedTrack;
    } catch (error) {
      console.error("Failed to initialize DtlnNoiseSuppressionProcessor:", error);
      this.stopProcessing();
      throw error;
    }
  }

  stopProcessing(): void {
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
    if (this.destinationNode) {
      this.destinationNode.disconnect();
      this.destinationNode = null;
    }
    if (this.audioContext) {
      if (this.audioContext.state !== "closed") {
        this.audioContext.close().catch((err) => {
          console.warn("Error closing AudioContext:", err);
        });
      }
      this.audioContext = null;
    }
    if (this.processedTrack) {
      this.processedTrack.stop();
      this.processedTrack = null;
    }
  }
}
